import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { COOKIE_IDS, KEYS, SERVER_ERROR_MESSAGES } from '../Constants/Constants';
import { deleteCookie, getCookie } from '../Utils/CookieUtils';
import { isEmptyObject } from '../Utils/Basic';
import { msalInstance } from '../Components/CustomMsalProvider/CustomMsalProvider';
import { getTokenFromAzure } from '../Utils/TokenManager';
import { toast } from 'react-toastify';

// Define a type for the request config
type RequestConfig = AxiosRequestConfig & {
  url: string;
  method: 'get' | 'post' | 'put' | 'delete';
  data?: any;
  headers?: Record<string, string>;
  query?: any;
};

// Create an Axios instance with default configurations
const SUBSCRIPTION_KEY = KEYS.SUBSCRIPTION_KEY_ENCLAVE;
const axiosInstance: AxiosInstance = axios.create({
  baseURL: KEYS.API_HOST_ENCLAVE,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '-1',
    'X-XSS-Protection': '1; mode=block',
    'X-Frame-Options': 'SAMEORIGIN',
    'Content-Security-Policy': "script-src 'self'",
    'X-Content-Type-Options': 'nosniff',
    Allow: 'GET, POST, HEAD',
    // "Content-Encoding": "deflate, gzip",
    'Ocp-Apim-Subscription-Key': SUBSCRIPTION_KEY,
  },
  timeout: KEYS.API_REQUEST_TIMEOUT_IN_SEC * 1000,
  timeoutErrorMessage: SERVER_ERROR_MESSAGES.timeoutMsg,
});

// Add request interceptors for custom headers and URL generation
axiosInstance.interceptors.request.use(
  async (config: any) => {
    const token = getCookie('id_token')
      ? getCookie('id_token')
      : await getTokenFromAzure(msalInstance);
    if (config.url) {
      let _url = `https://${config.baseURL}${config.url}`;
      config.url =
        config?.query && !isEmptyObject(config.query)
          ? _url +
            '?' +
            Object.entries(config.query)
              .map((item) => {
                return item.join('=');
              })
              .join('&')
          : _url;
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: 'Bearer ' + token,
        };
      }
    } else {
      throw new Error('key value of End point URL is not matching with provided key name.');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptors for error handling and data processing
axiosInstance.interceptors.response.use(
  (response) => {
    // Add any response data processing here
    return response?.data;
  },
  (error) => {
    // Handle Axios-specific errors
    if (axios.isAxiosError(error)) {
      // Add custom error handling logic here
      if (error.code === 'ECONNABORTED') {
        toast(error.message);
        return Promise.reject(error);
      }
      const errorStatus: number = error?.response?.status as number;
      if (errorStatus === 401) {
        deleteCookie('id_token');
        deleteCookie('unique_id');
        deleteCookie('session_info');
        deleteCookie(COOKIE_IDS?.PREFERRED_TIME_FORMAT);
        console.error('Authorization error:', error.message);
        toast(SERVER_ERROR_MESSAGES.unAuthorizedMsg);
        setTimeout(() => {
          msalInstance.logoutRedirect();
        }, 1500);
      } else {
        console.error('Misc error:', error.message);
        toast(SERVER_ERROR_MESSAGES.generalMsg);
      }
    } else {
      // Handle other unexpected errors
      console.error('Unexpected error:', error.message);
      toast(SERVER_ERROR_MESSAGES.generalMsg);
    }
    return Promise.reject(error);
  },
);

// Define a helper function for making requests
async function APIClientEnclave<T = any>(config: RequestConfig): Promise<AxiosResponse<T>> {
  try {
    const response = await axiosInstance.request<T>(config);
    return response;
  } catch (error) {
    throw error;
  }
}

// Export the helper function
export default APIClientEnclave;
